/*
 * Investment Dropdown
 * */

import classNames from 'classnames';
import PropTypes from 'prop-types';
// BASE
import React, { useCallback, useMemo } from 'react';

import { loadState } from 'app-components/hooks/utils';

import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
// MUI COMPONENTS
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

//  CUSTOM
import LoadingView from 'components/LoadingView';
import ZeroStateView from 'components/ZeroStateView';

// RELATIVE
import useStyles from './styles';

const BootstrapInput = withStyles(InputBase, (theme) => ({
  input: {
    padding: '8px 0px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.main}`,
    display: 'flex',
    flexDirection: 'row',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.mainr,
    },
    '&:focus': {
      marginLeft: -1, //  So the text inside isn't slightly moved every time the border px changes
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
    },
  },
}));

const DisabledBootstrapInput = withStyles(InputBase, (theme) => ({
  input: {
    padding: '8px 0px',
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    display: 'flex',
    color: theme.palette.text.disabled,
    flexDirection: 'row',
  },
}));

/* Component */
const SimplifiStyledSelect = (props) => {
  const {
    onOpen,
    MenuProps,
    id,
    open,
    input,
    className,
    overrideClass,
    multiple,
    value,
    onClose,
    onChange,
    IconComponent,
    displayEmpty,
    renderValue,
    children,
    viewState,
    disabled,
    showZeroState,
    sharedcomponentid,
    ...other
  } = props;
  const { classes } = useStyles();

  const formatRenderValue = useCallback(() => {
    const renderItem = renderValue(value);
    if (typeof renderItem === 'string') {
      return (
        <div className={classes.textContainer}>
          <Typography className={classes.textBox}>{renderValue(value)}</Typography>
        </div>
      );
    }
    return renderItem;
  }, [value, classes, renderValue]);

  const emptyView = useMemo(
    () => (
      <ZeroStateView
        size='small'
        id='simplifi-select-zero-state'
        title='No data'
        description='You currently have no data to display'
        qIllustration='emptyBox'
      />
    ),
    [],
  );

  const failedToFetch = useMemo(
    () => (
      <ZeroStateView
        size='small'
        id='simplifi-select-failed-to-fetch'
        title='Failed to fetch data'
        description='Please refresh the app to try again'
        qIllustration='planeCrash'
      />
    ),
    [],
  );

  const loadingView = useMemo(() => <LoadingView size={50} className={classes.zeroStyles} />, [classes]);

  //  This is used because there is a bug that allows multiple states to show at once even with only one viewState variable
  const pickStateToDisplay = useMemo(() => {
    if (loadState.noDataStates.includes(viewState) || showZeroState) {
      return emptyView;
    }

    if (loadState.tryAgainStates.includes(viewState) || loadState.updateRequiredStates.includes(viewState)) {
      return failedToFetch;
    }

    if (loadState.skeletonStates.includes(viewState)) {
      return loadingView;
    }

    return children;
  }, [children, emptyView, failedToFetch, loadingView, showZeroState, viewState]);

  return (
    <FormControl
      sharedcomponentid={sharedcomponentid}
      variant='filled'
      className={!overrideClass ? classNames(classes.formControl, className) : className}
    >
      <Select
        disabled={disabled}
        sharedcomponentid='SIMPLIFI_STYLED_SELECT'
        onOpen={onOpen}
        MenuProps={MenuProps}
        id={id}
        open={open}
        input={disabled ? <DisabledBootstrapInput id={`${id}-input`} /> : <BootstrapInput id={`${id}-input`} />}
        className={classes.formBox}
        multiple={multiple}
        value={value}
        onClose={onClose}
        onChange={onChange}
        IconComponent={IconComponent || ExpandMoreIcon}
        displayEmpty={displayEmpty}
        renderValue={typeof renderValue === 'function' ? formatRenderValue : renderValue}
        {...other}
      >
        {pickStateToDisplay}
      </Select>
    </FormControl>
  );
};

SimplifiStyledSelect.propTypes = {
  onOpen: PropTypes.func,
  MenuProps: PropTypes.object,
  id: PropTypes.string,
  open: PropTypes.bool,
  input: PropTypes.any,
  className: PropTypes.string,
  multiple: PropTypes.bool,
  value: PropTypes.any,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  IconComponent: PropTypes.any,
  displayEmpty: PropTypes.any,
  renderValue: PropTypes.any,
  children: PropTypes.any,
  overrideClass: PropTypes.bool,
  viewState: PropTypes.string,
  disabled: PropTypes.bool,
  showZeroState: PropTypes.bool,
  sharedcomponentid: PropTypes.string,
};

export default SimplifiStyledSelect;
